import React, { useState, useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useAuth0 } from "@auth0/auth0-react"
import { updateNurseScheduleForDate } from "../../../../features/futureHuddles/futureSchedulesSlice"
import {
	getClinicTimes,
	convertMinutesToMilitaryTime,
	getCurrentTime,
	getScheduleLockTime,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow,
	getDateFormatted
} from "../../../../utils/generalUtils"
import StaffingEntryTable from "../../../DailyCalendarFiles/StaffingEntryTable"
import NotificationPopup from "../../../DailyCalendarFiles/CustomCalendar/NotificationPopup"
import useLogging from "../../../../hooks/useLogging"
import useFetchSettings from "../../../../hooks/useFetchSettings"
import { FaLock } from "react-icons/fa"

// Default shift per role for future staffing entry (module-level so the
// reference is stable and doesn't re-trigger the shift-default effect).
const FUTURE_SHIFT_BY_ROLE = { RN: "7:30 am - 6:00 pm", LVN: "7:30 am - 4:00 pm" }

const NurseScheduler = ({ day, settings }) => {
	const dispatch = useDispatch()
	const logging = useLogging()
	const { getAccessTokenSilently } = useAuth0()

	const futureSchedulesData = useSelector(state => state.futureSchedules.data)
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const { user } = useAuth0()
	// Future Huddle's nurse picker should only show RN/LVN — the
	// clinicalOnly variant narrows `settingsData.nurses` to those roles
	// while leaving everything else (centerOpen, holidays, etc.) intact.
	const { settingsData } = useFetchSettings({ clinicalOnly: true })
	const settingsNurses = settingsData?.nurses

	const [nurses, setNurses] = useState([])
	const [notification, setNotification] = useState({ visible: false, message: "", success: true })

	// Serialize auto-save writes: if a save is already in flight, stash the
	// latest nurse list and flush it once the current write resolves, so a
	// burst of edits collapses to one write and the newest state always wins.
	const savingRef = useRef(false)
	const pendingNursesRef = useRef(null)

	// Get settings data for lock time calculation
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	// Check if this is tomorrow's schedule
	const testEnvironment = useMemo(() => user.org_id === "org_lkg3WMhi3Q2l92iT", [user.org_id])

	const isTomorrowSchedule = useMemo(() => {
		if (!day || !day.date) return false

		const tomorrowDate = getFirstOpenDateTomorrow(
			centerOpen,
			testEnvironment,
			centerClosedDates
		)
		const tomorrowDateFormatted = getDateFormatted(tomorrowDate)

		return day.date === tomorrowDateFormatted
		// centerClosedDates is stable in practice (read from settings); wrapping each call site adds noise.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [day, centerOpen, testEnvironment, centerClosedDates])

	// Calculate lock time and current time for tomorrow's schedule
	const lockTime = useMemo(() => {
		if (!isTomorrowSchedule) return null

		const today = getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
		return getScheduleLockTime(
			today,
			settingsData?.close_time_minutes || [],
			settingsData?.holiday_close_time_minutes || [],
			settingsData?.holidays || []
		)
		// centerClosedDates is stable in practice (read from settings); wrapping each call site adds noise.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTomorrowSchedule, centerOpen, testEnvironment, centerClosedDates, settingsData])

	const currentTime = getCurrentTime(sandbox, testEnvironment)
	const isEnabled = !isTomorrowSchedule || currentTime < lockTime

	// Convert day.date to a Date object and calculate open/close times
	const dayDate = new Date(day.date + "T00:00:00")

	const { openTime, closeTime } = getClinicTimes(settings, dayDate)

	// Convert times to military format for the shared component
	const openTimeMilitary = convertMinutesToMilitaryTime(openTime)
	const closeTimeMilitary = convertMinutesToMilitaryTime(closeTime)

	// Get nurses from futureSchedules store when day changes
	useEffect(() => {
		if (!day || !day.date) return

		// Use the schedule from futureSchedules if available
		const daySchedule = futureSchedulesData[day.date]
		if (daySchedule && daySchedule.nurses) {
			// StaffingEntryTable now handles conversion internally, so just pass the raw data
			setNurses(daySchedule.nurses)
		} else {
			setNurses([])
		}
	}, [day, futureSchedulesData])

	// Callback functions for the shared StaffingEntryTable component
	const handleAddNurse = nurseToAdd => {
		if (!day || !day.date || !isEnabled) return

		// Update local state for immediate UI feedback
		const updatedNurses = [...nurses, nurseToAdd]
		setNurses(updatedNurses)

		// StaffingEntryTable already converted to storage format (minutes)
		dispatch(updateNurseScheduleForDate({ date: day.date, nurses: updatedNurses }))
		persistNurses(updatedNurses)
	}

	const handleUpdateNurse = (index, updatedNurse) => {
		if (!day || !day.date || !isEnabled) return

		// Update local state
		const updatedNurses = [...nurses]
		updatedNurses[index] = updatedNurse
		setNurses(updatedNurses)

		// StaffingEntryTable already converted to storage format (minutes)
		dispatch(updateNurseScheduleForDate({ date: day.date, nurses: updatedNurses }))
		persistNurses(updatedNurses)
	}

	const handleDeleteNurse = index => {
		if (!day || !day.date || !isEnabled) return

		// Update local state
		const updatedNurses = nurses.filter((_, i) => i !== index)
		setNurses(updatedNurses)

		// StaffingEntryTable already converted to storage format (minutes)
		dispatch(updateNurseScheduleForDate({ date: day.date, nurses: updatedNurses }))
		persistNurses(updatedNurses)
	}

	// Auto-save the future huddle nurse schedule to MongoDB.
	const persistNurses = async nursesToSave => {
		if (!day || !day.date || !isEnabled) return

		// A write is already in flight — remember the newest state and flush it
		// when the current one finishes, so writes never overlap or get lost.
		if (savingRef.current) {
			pendingNursesRef.current = nursesToSave
			return
		}
		savingRef.current = true

		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			// Filter out UI-only fields before saving to MongoDB
			const cleanNurses = nursesToSave.map(nurse => {
				const { defaultShiftLength, ...cleanNurse } = nurse
				return cleanNurse
			})

			const requestBody = {
				date: day.date,
				nurseSchedule: cleanNurses // Only nurses are managed here; patients/chairs are left untouched
			}

			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/api/update-schedule`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json"
					},
					body: JSON.stringify(requestBody)
				}
			)

			if (!response.ok) {
				let detail = ""
				try {
					detail = JSON.stringify(await response.json())
				} catch {
					detail = "<no body>"
				}
				throw new Error(`Failed to save schedule (HTTP ${response.status}): ${detail}`)
			}

			setNotification({ visible: true, message: "Staffing saved", success: true })
			logging.info("Future nurse schedule saved successfully", {
				date: day.date,
				nurses: cleanNurses.length
			})
		} catch (error) {
			setNotification({
				visible: true,
				message: "Error saving staffing. Please try again.",
				success: false
			})
			logging.error("Error saving future nurse schedule:", error)
		} finally {
			savingRef.current = false

			setTimeout(() => {
				setNotification({ visible: false, message: "", success: true })
			}, 3000)

			if (pendingNursesRef.current) {
				const next = pendingNursesRef.current
				pendingNursesRef.current = null
				persistNurses(next)
			}
		}
	}

	if (!settingsNurses) {
		return (
			<div className="bg-white rounded-lg shadow p-5">
				<div className="text-center py-6 text-gray-500">Loading nurse data...</div>
			</div>
		)
	}

	return (
		<div className="bg-white rounded-lg shadow p-5">
			{/* Important Notice */}
			<div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200">
				<div className="flex items-start">
					<div className="flex-shrink-0">
						<svg
							className="h-5 w-5 text-amber-400"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fillRule="evenodd"
								d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
								clipRule="evenodd"
							/>
						</svg>
					</div>
					<div className="ml-3">
						<p className="text-sm text-amber-700">
							<strong>Important:</strong> If this staffing schedule is not updated
							with the proper number of nurses, the staffing summary will be incorrect
							for the staffing team.
						</p>
					</div>
				</div>
			</div>

			{/* Save notification as a top-of-page overlay so it doesn't shift the table */}
			{notification.visible && (
				<NotificationPopup
					message={notification.message}
					success={notification.success}
				/>
			)}

			<StaffingEntryTable
				nurses={nurses}
				onAddNurse={handleAddNurse}
				onUpdateNurse={handleUpdateNurse}
				onDeleteNurse={handleDeleteNurse}
				settingsNurses={settingsNurses}
				openTime={openTimeMilitary}
				closeTime={closeTimeMilitary}
				readOnly={!isEnabled}
				sandbox={sandbox}
				showAddRow={isEnabled}
				title={
					<span className="flex items-center justify-center">
						{!isEnabled && isTomorrowSchedule && (
							<span
								className="mr-2 text-gray-500 flex items-center"
								title="Nurse schedule can only be modified before 11 AM"
							>
								<FaLock className="h-5 w-5" />
							</span>
						)}
						Future Floor Nurse Staffing
					</span>
				}
				allowedRoles={["RN", "LVN"]}
				defaultShiftByRole={FUTURE_SHIFT_BY_ROLE}
			/>

			{/* Auto-save status */}
			<div className="flex justify-center mt-4 text-sm text-gray-500">
				{!isEnabled && isTomorrowSchedule
					? "Schedule is locked and can no longer be edited."
					: "Changes are saved automatically."}
			</div>
		</div>
	)
}

export default NurseScheduler
